import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  CarFront,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Fuel,
  Gauge,
  Cog,
  Search,
  RotateCcw,
  Settings2,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { CfaoVehicle } from "@/data/cfaoVehicles";
import { useVehicles } from "@/hooks/useVehicles";
import { matchesVehicleSearch, publicVehicles } from "@/utils/vehicleSearch";

const PAGE_SIZE = 9;
const MILEAGE_MIN = 0;
let inventoryState: Record<string, any> = {};
const unique = (values: string[]) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
const money = (value: number) => `R ${value.toLocaleString("en-ZA")}`;
const km = (value: number) => `${value.toLocaleString("en-ZA")} km`;
const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const featureAcronyms = new Map([
  ["abs", "ABS"], ["ebd", "EBD"], ["eba", "EBA"], ["usb", "USB"], ["pdc", "PDC"],
  ["led", "LED"], ["gps", "GPS"], ["fwd", "FWD"], ["rwd", "RWD"], ["gvm", "GVM"], ["gcm", "GCM"],
]);
const featureLabel = (value: string) => value.split("-").map((word) => featureAcronyms.get(word) || `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(" ");
const normal = (value: string) => value.trim().toLowerCase();
const driveKey = (value: string) => {
  const key = normal(value);
  if (["all", "awd", "4x4"].includes(key)) return "4x4";
  if (["front", "fwd"].includes(key)) return "fwd";
  if (["rear", "rwd", "fr"].includes(key)) return "rwd";
  return key;
};

const ToyotaTokai = () => (
  <div className="inventory-toyota" aria-label="Toyota Tokai — Driven by People">
    <svg viewBox="0 0 58 38" aria-hidden="true">
      <ellipse cx="29" cy="19" rx="24" ry="14" />
      <ellipse cx="29" cy="14" rx="9" ry="12" />
      <path d="M7 15c8 7 36 7 44 0M29 26V8" />
    </svg>
    <span><strong>TOYOTA TOKAI</strong><small>DRIVEN BY PEOPLE</small></span>
  </div>
);

const VehicleInventoryContent: React.FC<{ inventory: CfaoVehicle[] }> = ({ inventory: PUBLIC_VEHICLES }) => {
  const PRICE_LIMIT = Math.max(100000, Math.ceil(Math.max(...PUBLIC_VEHICLES.map((vehicle) => vehicle.price), 0) / 100000) * 100000);
  const YEAR_MIN = Math.min(...PUBLIC_VEHICLES.map((vehicle) => vehicle.year), new Date().getFullYear());
  const YEAR_MAX = Math.max(...PUBLIC_VEHICLES.map((vehicle) => vehicle.year), new Date().getFullYear());
  const MILEAGE_MAX = Math.max(10000, Math.ceil(Math.max(...PUBLIC_VEHICLES.map((vehicle) => vehicle.mileage), 0) / 10000) * 10000);
  const makes = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.make)), [PUBLIC_VEHICLES]);
  const models = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.model)), [PUBLIC_VEHICLES]);
  const bodyTypes = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.bodyType)), [PUBLIC_VEHICLES]);
  const transmissions = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.transmission)), [PUBLIC_VEHICLES]);
  const fuels = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.fuelType)), [PUBLIC_VEHICLES]);
  const colors = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.color).filter((color) => color !== "Please confirm")), [PUBLIC_VEHICLES]);
  const drives = useMemo(() => unique(PUBLIC_VEHICLES.map((v) => v.driveType)), [PUBLIC_VEHICLES]);
  const seats = useMemo(() => [...new Set(PUBLIC_VEHICLES.map((v) => v.seats).filter(Boolean))].sort((a, b) => a - b), [PUBLIC_VEHICLES]);
  const featureOptions = useMemo(() => unique(PUBLIC_VEHICLES.flatMap((vehicle) => vehicle.detail.features.map((feature) => slugify(feature.name)))), [PUBLIC_VEHICLES]);
  const featureCounts = useMemo(() => {
    const counts = new Map<string, number>();
    PUBLIC_VEHICLES.forEach((vehicle) => {
      const vehicleFeatures = new Set(vehicle.detail.features.map((feature) => slugify(feature.name)));
      vehicleFeatures.forEach((feature) => counts.set(feature, (counts.get(feature) || 0) + 1));
    });
    return counts;
  }, [PUBLIC_VEHICLES]);

  const [makeModel, setMakeModel] = useState(inventoryState.makeModel ?? "all");
  const [body, setBody] = useState(inventoryState.body ?? "all");
  const [transmission, setTransmission] = useState(inventoryState.transmission ?? "all");
  const [fuel, setFuel] = useState(inventoryState.fuel ?? "all");
  const [color, setColor] = useState(inventoryState.color ?? "all");
  const [drive, setDrive] = useState(inventoryState.drive ?? "any");
  const [seatCount, setSeatCount] = useState(inventoryState.seatCount ?? "all");
  const [minPrice, setMinPrice] = useState(inventoryState.minPrice ?? 0);
  const [maxPrice, setMaxPrice] = useState(inventoryState.maxPrice ?? PRICE_LIMIT);
  const [yearMin, setYearMin] = useState(inventoryState.yearMin ?? YEAR_MIN);
  const [yearMax, setYearMax] = useState(inventoryState.yearMax ?? YEAR_MAX);
  const [mileageMin, setMileageMin] = useState(inventoryState.mileageMin ?? MILEAGE_MIN);
  const [mileageMax, setMileageMax] = useState(inventoryState.mileageMax ?? MILEAGE_MAX);
  const [features, setFeatures] = useState<string[]>(inventoryState.features ?? []);
  const [featureSearch, setFeatureSearch] = useState("");
  const [sort, setSort] = useState(inventoryState.sort ?? "latest");
  const [searchQuery, setSearchQuery] = useState(inventoryState.searchQuery ?? "");
  const [page, setPage] = useState(inventoryState.page ?? 1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() => inventoryState.appliedFilters ?? {
    makeModel: inventoryState.makeModel ?? "all",
    body: inventoryState.body ?? "all",
    transmission: inventoryState.transmission ?? "all",
    fuel: inventoryState.fuel ?? "all",
    color: inventoryState.color ?? "all",
    drive: inventoryState.drive ?? "any",
    seatCount: inventoryState.seatCount ?? "all",
    minPrice: inventoryState.minPrice ?? 0,
    maxPrice: inventoryState.maxPrice ?? PRICE_LIMIT,
    yearMin: inventoryState.yearMin ?? YEAR_MIN,
    yearMax: inventoryState.yearMax ?? YEAR_MAX,
    mileageMin: inventoryState.mileageMin ?? MILEAGE_MIN,
    mileageMax: inventoryState.mileageMax ?? MILEAGE_MAX,
    features: inventoryState.features ?? [],
  });

  const filtered = useMemo(() => {
    const {
      makeModel: appliedMakeModel,
      body: appliedBody,
      transmission: appliedTransmission,
      fuel: appliedFuel,
      color: appliedColor,
      drive: appliedDrive,
      seatCount: appliedSeatCount,
      minPrice: appliedMinPrice,
      maxPrice: appliedMaxPrice,
      yearMin: appliedYearMin,
      yearMax: appliedYearMax,
      mileageMin: appliedMileageMin,
      mileageMax: appliedMileageMax,
      features: appliedFeatures,
    } = appliedFilters;
    const result = PUBLIC_VEHICLES.filter((v) => {
      if (!matchesVehicleSearch(v, searchQuery, "all")) return false;
      if (appliedMakeModel.startsWith("make:") && v.make !== appliedMakeModel.slice(5)) return false;
      if (appliedMakeModel.startsWith("model:") && v.model !== appliedMakeModel.slice(6)) return false;
      const selectedTransmission = normal(appliedTransmission);
      const transmissionMatch = appliedTransmission === "all"
        || normal(v.transmission) === selectedTransmission
        || (["cvt", "dct"].includes(selectedTransmission) && normal(`${v.model} ${v.fullTitle}`).includes(selectedTransmission))
        || (["electric", "hybrid"].includes(selectedTransmission) && normal(v.fuelType).includes(selectedTransmission));
      const vehicleFeatures = new Set(v.detail.features.map((feature) => slugify(feature.name)));
      return (appliedBody === "all" || normal(v.bodyType) === normal(appliedBody))
        && transmissionMatch
        && (appliedFuel === "all" || normal(v.fuelType) === normal(appliedFuel))
        && (appliedColor === "all" || normal(v.color) === normal(appliedColor))
        && (appliedDrive === "any" || driveKey(v.driveType) === driveKey(appliedDrive))
        && (appliedSeatCount === "all" || (appliedSeatCount === "8+" ? v.seats >= 8 : v.seats === Number(appliedSeatCount)))
        && v.price >= appliedMinPrice && v.price <= appliedMaxPrice
        && v.year >= appliedYearMin && v.year <= appliedYearMax
        && v.mileage >= appliedMileageMin && v.mileage <= appliedMileageMax
        && appliedFeatures.every((feature: string) => vehicleFeatures.has(feature));
    });
    if (sort === "price-low") result.sort((a, b) => a.price - b.price);
    if (sort === "price-high") result.sort((a, b) => b.price - a.price);
    if (sort === "mileage-low") result.sort((a, b) => a.mileage - b.mileage);
    if (sort === "year") result.sort((a, b) => b.year - a.year);
    if (sort === "latest") result.sort((a, b) => a.sourceOrder - b.sourceOrder);
    return result;
  }, [PUBLIC_VEHICLES, appliedFilters, searchQuery, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const filterKey = JSON.stringify([appliedFilters, sort]);
  const previousFilterKey = useRef(filterKey);
  useEffect(() => {
    if (previousFilterKey.current !== filterKey) setPage(1);
    previousFilterKey.current = filterKey;
  }, [filterKey]);
  useEffect(() => {
    inventoryState = {makeModel, body, transmission, fuel, color, drive, seatCount, minPrice, maxPrice, yearMin, yearMax, mileageMin, mileageMax, features, appliedFilters, searchQuery, sort, page};
  }, [makeModel, body, transmission, fuel, color, drive, seatCount, minPrice, maxPrice, yearMin, yearMax, mileageMin, mileageMax, features, appliedFilters, searchQuery, sort, page]);
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const vehicles = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setMakeModel("all"); setBody("all"); setTransmission("all"); setFuel("all");
    setColor("all"); setDrive("any"); setSeatCount("all"); setMinPrice(0);
    setMaxPrice(PRICE_LIMIT); setYearMin(YEAR_MIN); setYearMax(YEAR_MAX);
    setMileageMin(MILEAGE_MIN); setMileageMax(MILEAGE_MAX); setFeatures([]); setFeatureSearch("");
    setSearchQuery("");
    setAppliedFilters({ makeModel: "all", body: "all", transmission: "all", fuel: "all", color: "all", drive: "any", seatCount: "all", minPrice: 0, maxPrice: PRICE_LIMIT, yearMin: YEAR_MIN, yearMax: YEAR_MAX, mileageMin: MILEAGE_MIN, mileageMax: MILEAGE_MAX, features: [] }); setSort("latest"); setPage(1);
  };

  const applyFilters = () => {
    setAppliedFilters({ makeModel, body, transmission, fuel, color, drive, seatCount, minPrice, maxPrice, yearMin, yearMax, mileageMin, mileageMax, features });
    setPage(1);
    if (window.matchMedia("(max-width: 900px)").matches) {
      setFiltersOpen(false);
      requestAnimationFrame(() => document.getElementById("inventory-title")?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  const toggleFeature = (feature: string) => setFeatures((selected) => selected.includes(feature) ? selected.filter((item) => item !== feature) : [...selected, feature]);
  const visibleFeatures = featureOptions.filter((feature) => featureLabel(feature).toLowerCase().includes(featureSearch.trim().toLowerCase()));

  const select = (label: string, value: string, setter: (value: string) => void, options: (string | number)[], placeholder: string) => (
    <label className="inventory-field"><span>{label}</span><select value={value} onChange={(e) => setter(e.target.value)}><option value="all">{placeholder}</option>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
  );

  const pageNumbers = Array.from({ length: pages }, (_, i) => i + 1).filter((number) => number === 1 || number === pages || Math.abs(number - page) <= 2);

  return (
    <section id="vehicle-search" className="inventory-section" aria-labelledby="inventory-title">
      <div className="container-x inventory-shell">
        <aside className="inventory-filters">
          <header><span><SlidersHorizontal /></span><div><h2>Filter Vehicles</h2><p>Refine your search to find your perfect Toyota.</p></div></header>
          <button
            type="button"
            className="inventory-filter-toggle"
            aria-expanded={filtersOpen}
            aria-controls="inventory-filter-controls"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <Settings2 /> {filtersOpen ? "Hide filters" : "Show filters"}
            <ChevronRight aria-hidden="true" />
          </button>
          <div id="inventory-filter-controls" className={`inventory-filter-controls ${filtersOpen ? "is-open" : ""}`}>
          <label className="inventory-field"><span>Make / Model</span><select value={makeModel} onChange={(e) => setMakeModel(e.target.value)}><option value="all">Choose Make / Model</option><optgroup label="Makes">{makes.map((make) => <option key={make} value={`make:${make}`}>{make}</option>)}</optgroup><optgroup label="Models">{models.map((model) => <option key={model} value={`model:${model}`}>{model}</option>)}</optgroup></select></label>
          {select("Body Type", body, setBody, bodyTypes, "Choose Body Type")}
          <div className="inventory-price"><span>Price Range</span><div className="inventory-range" style={{ "--range-start": `${(minPrice / PRICE_LIMIT) * 100}%`, "--range-end": `${(maxPrice / PRICE_LIMIT) * 100}%` } as React.CSSProperties}><div /><input aria-label="Minimum price" type="range" min="0" max={PRICE_LIMIT} step="10000" value={minPrice} onChange={(e) => setMinPrice(Math.min(Number(e.target.value), maxPrice - 10000))} /><input aria-label="Maximum price" type="range" min="0" max={PRICE_LIMIT} step="10000" value={maxPrice} onChange={(e) => setMaxPrice(Math.max(Number(e.target.value), minPrice + 10000))} /></div><div><small>{money(minPrice)}</small><small>{money(maxPrice)}</small></div></div>
          {select("Transmission", transmission, setTransmission, transmissions, "Choose Transmission")}
          {select("Fuel Type", fuel, setFuel, fuels, "Choose Fuel Type")}
          <div className="inventory-price"><span>Year Range</span><div className="inventory-range" style={{ "--range-start": `${((yearMin - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * 100}%`, "--range-end": `${((yearMax - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * 100}%` } as React.CSSProperties}><div /><input aria-label="Minimum year" type="range" min={YEAR_MIN} max={YEAR_MAX} step="1" value={yearMin} onChange={(e) => setYearMin(Math.min(Number(e.target.value), yearMax - 1))} /><input aria-label="Maximum year" type="range" min={YEAR_MIN} max={YEAR_MAX} step="1" value={yearMax} onChange={(e) => setYearMax(Math.max(Number(e.target.value), yearMin + 1))} /></div><div><small>{yearMin}</small><small>{yearMax}</small></div></div>
          <div className="inventory-price"><span>Mileage (km)</span><div className="inventory-range" style={{ "--range-start": `${(mileageMin / MILEAGE_MAX) * 100}%`, "--range-end": `${(mileageMax / MILEAGE_MAX) * 100}%` } as React.CSSProperties}><div /><input aria-label="Minimum mileage" type="range" min={MILEAGE_MIN} max={MILEAGE_MAX} step="5000" value={mileageMin} onChange={(e) => setMileageMin(Math.min(Number(e.target.value), mileageMax - 5000))} /><input aria-label="Maximum mileage" type="range" min={MILEAGE_MIN} max={MILEAGE_MAX} step="5000" value={mileageMax} onChange={(e) => setMileageMax(Math.max(Number(e.target.value), mileageMin + 5000))} /></div><div><small>{mileageMin.toLocaleString("en-ZA")}</small><small>{mileageMax.toLocaleString("en-ZA")}</small></div></div>
          {select("Color", color, setColor, colors, "Choose Color")}
          <label className="inventory-field"><span>Drive Type</span><select value={drive} onChange={(e) => setDrive(e.target.value)}><option value="any">Choose Drive Type</option>{drives.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <div className="inventory-feature-field"><span>Popular Features</span><details className="inventory-feature-filter">
            <summary><span>{features.length ? `${features.length} feature${features.length === 1 ? "" : "s"} selected` : "Choose Features"}</span><ChevronDown aria-hidden="true" /></summary>
            <div className="inventory-feature-menu">
              <label className="inventory-feature-search"><Search aria-hidden="true" /><input type="search" value={featureSearch} onChange={(event) => setFeatureSearch(event.target.value)} placeholder="Search features..." aria-label="Search vehicle features" /></label>
              <div className="inventory-feature-options">
                {visibleFeatures.map((feature) => <label key={feature}><input type="checkbox" checked={features.includes(feature)} onChange={() => toggleFeature(feature)} /><span><i>{features.includes(feature) && <Check aria-hidden="true" />}</i>{featureLabel(feature)}</span><small>{featureCounts.get(feature) || 0}</small></label>)}
                {!visibleFeatures.length && <p>No features found.</p>}
              </div>
            </div>
          </details></div>
          {select("Number of Seats", seatCount, setSeatCount, seats, "Any")}
          <div className="inventory-filter-actions">
            <button type="button" className="inventory-apply" onClick={applyFilters}><Search /> Apply Filters</button>
            <button type="button" className="inventory-reset" onClick={reset}><RotateCcw /> Reset Filters</button>
          </div>
          </div>
        </aside>

        <div className="inventory-results">
          <header className="inventory-heading"><div><p>All Vehicles</p><h2 id="inventory-title">Quality Vehicles, Personally Recommended</h2><span>Explore our hand-selected range of certified pre-owned Toyota vehicles at Toyota Tokai.</span></div><ToyotaTokai /></header>
          <div className="inventory-search" role="search" aria-label="Search vehicle stock">
            <label>
              <Search aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by make, model, year, body type, price or transmission"
                aria-label="Search vehicles"
              />
              {searchQuery && <button type="button" onClick={() => setSearchQuery("")} aria-label="Clear vehicle search"><X /></button>}
            </label>
            <label className="inventory-search-sort">Sort:<select aria-label="Sort vehicles" value={sort} onChange={(e) => setSort(e.target.value)}><option value="latest">Latest</option><option value="price-low">Price (Low to High)</option><option value="price-high">Price (High to Low)</option><option value="mileage-low">Mileage (Low to High)</option><option value="year">Year (New to Old)</option></select></label>
          </div>
          <div className="inventory-toolbar"><p>Showing <strong>{vehicles.length}</strong> of <strong>{filtered.length}</strong> vehicles</p></div>
          {vehicles.length ? <div className="inventory-grid">{vehicles.map((vehicle) => <article className="inventory-card" key={vehicle.id}>
            <Link to={`/vehicles/${vehicle.url.split("/").filter(Boolean).pop()}/`} className="inventory-card-image">
              <img className="inventory-card-image-photo" src={vehicle.image} alt={vehicle.fullTitle} loading="lazy" />
              {vehicle.badge && <span>{vehicle.badge}</span>}
            </Link>
            <div className="inventory-card-body">
              <span className="inventory-card-stock">{vehicle.id}</span>
              <div className="inventory-card-title"><h3>{vehicle.fullTitle}</h3>{vehicle.rating && <strong><Star />{vehicle.rating.toFixed(1)}</strong>}</div>
              <div className="inventory-card-pricing">
                {vehicle.previousPrice && <small>Was {money(vehicle.previousPrice)}</small>}
                <p>{money(vehicle.price)}</p>
                {vehicle.priceDropAmount > 0 && <small>Price dropped by {money(vehicle.priceDropAmount)}</small>}
                {vehicle.monthlyPayment && <small>From {money(vehicle.monthlyPayment)} p/m</small>}
                <b>Finance Available</b>
              </div>
              <dl>
                <div><Gauge /><span>{km(vehicle.mileage)}</span></div>
                <div><Settings2 /><span>{vehicle.transmission}</span></div>
                <div><Fuel /><span>{vehicle.fuelType}</span></div>
                <div><CarFront /><span>{vehicle.bodyType}</span></div>
                <div><Cog /><span>{vehicle.engineSize ? `${vehicle.engineSize} L` : "Confirm"}</span></div>
              </dl>
              <Link to={`/vehicles/${vehicle.url.split("/").filter(Boolean).pop()}/`}>View Details <ArrowRight /></Link>
            </div>
          </article>)}</div> : <div className="inventory-empty">No vehicles match these filters. <button onClick={reset}>Reset filters</button></div>}
          <footer className="inventory-pagination"><button aria-label="Previous page" disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}><ChevronLeft /></button>{pageNumbers.map((number, index) => <React.Fragment key={number}>{index > 0 && number - pageNumbers[index - 1] > 1 && <span>…</span>}<button className={page === number ? "active" : ""} onClick={() => setPage(number)}>{number}</button></React.Fragment>)}<button aria-label="Next page" disabled={page === pages} onClick={() => setPage((value) => Math.min(pages, value + 1))}><ChevronRight /></button><label>Vehicles per page:<select value={PAGE_SIZE} disabled><option>{PAGE_SIZE}</option></select></label></footer>
        </div>
        <div className="inventory-signoff"><span><CarFront /> Quality Toyota vehicles. A better tomorrow together.</span><strong>Certified&nbsp;&nbsp; | &nbsp;&nbsp;Trusted&nbsp;&nbsp; | &nbsp;&nbsp;Ready for more</strong></div>
      </div>
    </section>
  );
};

export const VehicleInventory: React.FC = () => {
  const { vehicles, loading, error } = useVehicles();
  if (loading) return <section className="inventory-section"><div className="container-x inventory-empty">Loading vehicles...</div></section>;
  if (error) return <section className="inventory-section"><div className="container-x inventory-empty">Unable to load vehicles. Please refresh the page.</div></section>;
  return <VehicleInventoryContent inventory={publicVehicles(vehicles)} />;
};

export default VehicleInventory;
